import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Keyboard, StyleSheet, View } from 'react-native';
import Alert from 'utils/packages/Alert';
import { emojify } from 'node-emoji';
import { format } from 'date-fns';
import Animated from 'react-native-reanimated';
import { getGroupById, importGroupTransactions, saveGroupTransaction, setSpendingGroupInfo, getConvertCurrency, } from 'actions';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Amplitude from 'utils/amplitude';
import { formatPlural } from 'utils/formatting';
import { AppView, CardView, LineSeparator, SettingsItemInput, SettingsItemNavigate, SettingsSection, } from 'design-system';
import { IconBoldCalendar, IconCoinStack, IconNote, IconProfile, } from 'design-system/icons';
import { muteErrorAndFetching, useDateTimePicker, useFetchOld, useKeyboardAdjustPan, useTextInput, } from 'hooks';
import { PaidByModal } from 'features/group/components/PaidByModal';
import RowTransaction from 'features/transactions/components/RowTransaction';
import StatusBar from 'design-system/StatusBar';
import useColors from 'hooks/useColors';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { selectGroupAnalytics, selectGroupById, selectUser, selectUserId, } from 'reducers/selectors';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import IconTransaction from 'design-system/icons/IconTransaction';
import { CustomSplitType, Sign } from 'utils/types/navigation';
import { IMPORT_GROUP_TRANSACTIONS_SUCCESS, SAVE_GROUP_TRANSACTION_SUCCESS, } from 'actions/types';
import { AmountSplit } from './utils/amountSplit';
import GroupTransactionSplit from './components/GroupTransactionSplit';
const GroupExpenseScreen = withAnimatedNavigationHeader(({ route, navigation, onScroll }) => {
    useKeyboardAdjustPan();
    const { groupId, addTransactionCallback } = route.params;
    const transactions = 'transactions' in route.params ? route.params.transactions : undefined;
    const [offlineAmount, offlineCurrency] = 'offlineAmount' in route.params
        ? [route.params.offlineAmount, route.params.offlineCurrency]
        : [];
    const group = useAppSelector((store) => selectGroupById(store, groupId));
    const groupAnalytics = useAppSelector((store) => selectGroupAnalytics(store, groupId));
    const user = useAppSelector(selectUser);
    const filteredUsers = useMemo(() => groupAnalytics?.users?.filter((u) => !u.deleted), [groupAnalytics]);
    const [isDefaultSplit, setIsDefaultSplit] = useState(true);
    const [desc, setDesc, , descRef] = useTextInput();
    const [date, setDate] = useState(new Date());
    const [paidBy, setPaidBy] = useState(filteredUsers?.find((u) => u.id === user.id) || user);
    useAutoFocusInput(descRef);
    const totalSpend = useMemo(() => (transactions
        ? transactions.reduce((prev, curr) => prev + curr.amount, 0)
        : offlineAmount) ?? 0, [offlineAmount, transactions]);
    const [amountSplit, setAmountSplit] = useState(() => AmountSplit.toEqualSplit(totalSpend, group?.userIds, group?.userIds));
    const [splitType, setSplitType] = useState(CustomSplitType.AMOUNT);
    const selectedUsers = useMemo(() => Object.entries(amountSplit)
        .filter(([, share]) => Number(share) > 0)
        .map(([userId]) => Number(userId)), [amountSplit]);
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (!isFirstRender.current) {
            setAmountSplit(AmountSplit.toEqualSplit(totalSpend, group?.userIds, selectedUsers));
        }
        isFirstRender.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalSpend, group?.userIds]);
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const [isFetching, setFetching] = useState(false);
    const onSaveTransactions = useCallback(async () => {
        if (!group || !transactions)
            return;
        setFetching(true);
        const res = await dispatch(importGroupTransactions(group.id, transactions.map((t) => t.id), AmountSplit.toNumber(amountSplit)));
        if (res?.type !== IMPORT_GROUP_TRANSACTIONS_SUCCESS) {
            setFetching(false);
            return;
        }
        Amplitude.logEvent('Created Group Transaction', { type: 'native' });
        await dispatch(getGroupById(group.id));
        res.payload.transactions.forEach((transaction) => {
            const txId = transaction.originalTransactionId;
            const cached = txId
                ? store.getState().transactions.transactionCache?.[txId]
                : undefined;
            const existing = (cached && 'spendingGroupInfo' in cached
                ? cached.spendingGroupInfo?.groups
                : undefined) ?? [];
            dispatch(setSpendingGroupInfo(transaction.originalTransactionId, {
                groups: [...existing, { group, transaction }],
            }));
        });
        setFetching(false);
        navigation.pop(2);
        addTransactionCallback?.(group.name);
    }, [group, transactions, amountSplit, addTransactionCallback]);
    const onSaveOffline = useCallback(async () => {
        if (!group || !offlineAmount || !offlineCurrency)
            return;
        if (!desc?.trim()) {
            Alert.alert(emojify('Hold on :dizzy:'), 'You must add a reference.', [
                {
                    text: 'Ok',
                    onPress: () => descRef.current?.focus(),
                },
            ]);
            return;
        }
        Keyboard.dismiss();
        setFetching(true);
        const res = await dispatch(saveGroupTransaction(group.id, {
            date: format(date, 'yyyy-MM-dd HH:mm:ss'),
            amount: offlineAmount,
            currency: offlineCurrency,
            counterpartName: desc,
            paidBy: paidBy.id,
            split: AmountSplit.toNumber(amountSplit),
        }));
        if (res?.type !== SAVE_GROUP_TRANSACTION_SUCCESS) {
            setFetching(false);
            return;
        }
        Amplitude.logEvent('Created Group Transaction', { type: 'custom' });
        await dispatch(getGroupById(group.id));
        setFetching(false);
        navigation.pop(1);
    }, [
        group,
        desc,
        date,
        offlineAmount,
        offlineCurrency,
        paidBy.id,
        amountSplit,
        descRef,
    ]);
    const onSaveCustomSplit = useCallback((customSplit, splitType) => {
        setAmountSplit(customSplit);
        setSplitType(splitType);
        setIsDefaultSplit(false);
    }, []);
    const onSelect = useCallback((user) => {
        const selected = selectedUsers.includes(user.id);
        if (selected) {
            setAmountSplit(AmountSplit.toEqualSplit(totalSpend, group?.userIds, selectedUsers.filter((id) => id !== user.id)));
            setIsDefaultSplit(true);
        }
        else {
            setAmountSplit(AmountSplit.toEqualSplit(totalSpend, group?.userIds, [
                ...selectedUsers,
                user.id,
            ]));
            setIsDefaultSplit(true);
        }
    }, [group?.userIds, selectedUsers, totalSpend]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const [result, , , fetch] = useFetchOld(muteErrorAndFetching);
    useEffect(() => {
        if (offlineCurrency &&
            groupAnalytics?.currency &&
            groupAnalytics.currency !== offlineCurrency) {
            fetch(getConvertCurrency({
                realTime: true,
                baseCurrency: offlineCurrency,
                quoteCurrency: groupAnalytics.currency,
            }));
        }
    }, [groupAnalytics?.currency, offlineCurrency]);
    if (!group)
        return null;
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle} onScroll={onScroll} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag">
          {transactions ? (<TransactionsSection transactions={transactions} totalSpend={totalSpend}/>) : (<OfflineTransactionSection groupId={groupId} date={date} setDate={setDate} amount={offlineAmount} currency={offlineCurrency} paidBy={paidBy} setPaidBy={setPaidBy} descRef={descRef} desc={desc} setDesc={setDesc} users={filteredUsers}/>)}
          <GroupTransactionSplit users={filteredUsers || []} total={totalSpend} currency={offlineCurrency || groupAnalytics?.currency} convertedCurrency={groupAnalytics?.currency} conversionRate={result?.results?.[0]?.exchangeRate} onSave={onSaveCustomSplit} amountSplit={amountSplit} splitType={splitType} paidBy={paidBy} onSelect={onSelect} isDefaultSplit={isDefaultSplit}/>
        </Animated.ScrollView>
        <Button brand floating disabled={!selectedUsers.length ||
            (selectedUsers.length === 1 && selectedUsers[0] === paidBy.id)} title={transactions
            ? formatPlural('Add transaction', `Add (${transactions.length}) transactions`, transactions.length)
            : 'Add a transaction'} isFetching={isFetching} onPress={transactions ? onSaveTransactions : onSaveOffline}/>
      </AppView>);
}, ({ colors }) => ({
    title: 'Split',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const TransactionsSection = ({ transactions, totalSpend, }) => (<CardView noMarginBottom>
    {transactions.map((item) => (<RowTransaction key={item.id} item={item} showDate disabled backgroundDark datePosition="right" showCategory={false}/>))}
    {transactions.length > 1 && (<>
        <LineSeparator noMargin/>
        <View style={styles.totalSpend}>
          <Text Text-16>Total amount</Text>
          <Amount Numbers-16 value={{
            amount: totalSpend,
            currency: transactions[0].currency,
        }}/>
        </View>
      </>)}
  </CardView>);
const OfflineTransactionSection = ({ groupId, date, setDate, amount, currency, paidBy, setPaidBy, descRef, desc, setDesc, users, }) => {
    const colors = useColors();
    const userId = useAppSelector(selectUserId);
    const { showDateTimePicker } = useDateTimePicker({
        initialValue: date,
        onSubmitDate: (date) => setDate(date),
    });
    const getPaidByTitle = (paidBy) => {
        if (!paidBy) {
            return 'Choose..';
        }
        if (paidBy.id === userId) {
            return 'You';
        }
        return 'handle' in paidBy ? `@${paidBy.handle}` : '?';
    };
    const navigation = useMainStackNavigation();
    const onPressAmount = useCallback(() => {
        navigation.push('AddBalance', {
            custom: {
                title: 'What’s the amount for this\ntransaction?',
                // only the web version of AddBalance reads this
                webSubtitle: 'What’s the amount for this transaction?',
                button: 'Continue',
                swapText: {
                    [Sign.Positive]: 'Swap to spending',
                    [Sign.Negative]: 'Swap to income',
                },
                defaultValue: amount,
                defaultCurrency: currency,
                icon: (<IconTransaction width={32} height={32} color={colors.elements.brand}/>),
            },
            onContinue: (amount, currency) => {
                if (amount === 0) {
                    Alert.alert(emojify('Hold on :dizzy:'), 'You must add an amount.', [
                        { text: 'Ok' },
                    ]);
                    return;
                }
                navigation.navigateDeprecated('GroupExpense', {
                    groupId,
                    offlineAmount: amount,
                    offlineCurrency: currency,
                });
            },
        });
    }, [amount, colors.elements.brand, currency, groupId]);
    return (<SettingsSection>
      <SettingsItemNavigate icon={<IconCoinStack color={colors.elements.brand}/>} description="Amount" title={<Amount Numbers-16 value={{ amount, currency }}/>} onPress={onPressAmount}/>

      <SettingsItemInput icon={<IconNote gradientColors={colors.gradients.lightPink.colors}/>} ref={descRef} onChangeText={setDesc} placeholder="Type reference" value={desc} description="Reference" returnKeyType="done"/>

      <SettingsItemNavigate icon={<IconBoldCalendar gradientColors={colors.gradients.peach.colors}/>} onPress={showDateTimePicker} title={date ? format(date, 'dd/MM/yyyy') : 'Choose'} description="Date"/>
      <SettingsItemNavigate icon={<IconProfile />} title={getPaidByTitle(paidBy)} onPress={() => {
            Keyboard.dismiss();
            PaidByModal.show({
                onSelected: setPaidBy,
                users,
                selectedUser: paidBy,
                options: {
                    title: amount > 0 ? 'Received by' : 'Paid by',
                },
            });
        }} description={amount > 0 ? 'Received by' : 'Paid by'}/>
    </SettingsSection>);
};
const styles = StyleSheet.create({
    totalSpend: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: rem(16),
    },
});
export default GroupExpenseScreen;
